/**
 * Menção com @ na comunicação interna (spec 037, PR C).
 *
 * - Escrever `@` no editor da comunicação INTERNA abre o autocompletar da
 *   EQUIPE do tenant (GET /tickets/:n/mentionable — o mesmo `tenantTeam` do
 *   seletor de responsável do #46). Quem não pode ser mencionado neste ticket
 *   aparece cinza, com o motivo ("não abre este ticket", "não vê a comunicação
 *   interna"), e não é escolhível: o autor é avisado ANTES de enviar. A API
 *   confere de novo e recusa o comentário inteiro (422) — nada calado.
 * - O texto continua texto: escolher alguém escreve `@Nome` no corpo. As
 *   menções que valem são as escolhidas cujo `@Nome` ainda está no texto.
 * - Na leitura, `@Nome` de quem foi mencionado vira um chip.
 */
import { useEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { keepPreviousData, queryOptions, useQuery } from '@tanstack/react-query';
import { AtSign } from 'lucide-react';
import { MENTION_BLOCK_LABELS, type Mentionable } from '@erp/shared';
import { api } from '@/lib/api-client';
import { cn } from '@/lib/utils';

export interface PickedMention {
  id: number;
  name: string;
}

const mentionableQuery = (ticketNumber: number, q: string) =>
  queryOptions({
    queryKey: ['erp', 'tickets', 'mentionable', { ticketNumber, q }] as const,
    queryFn: async ({ signal }): Promise<Mentionable> => {
      const res = await api<Mentionable>(`/tickets/${ticketNumber}/mentionable`, { query: { q: q || undefined }, signal });
      if (res.error) throw res.error;
      return res.data;
    },
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });

/** o `@busca` que termina no cursor: começo do texto ou espaço antes do `@`, sem quebra de linha */
export function activeMentionQuery(text: string, caret: number): { start: number; query: string } | null {
  const before = text.slice(0, caret);
  const at = before.lastIndexOf('@');
  if (at < 0) return null;
  if (at > 0 && !/\s/.test(before[at - 1]!)) return null;
  const query = before.slice(at + 1);
  if (query.length > 40 || /[\n@]/.test(query)) return null;
  return { start: at, query };
}

/** as escolhidas que ainda estão no texto, sem repetir */
export function mentionsInText(text: string, picked: readonly PickedMention[]): number[] {
  return [...new Set(picked.filter((p) => text.includes(`@${p.name}`)).map((p) => p.id))];
}

/** as recusas da API, em português (o código vem em `error`) */
export function mentionErrorMessage(code: string | undefined): string | null {
  switch (code) {
    case 'mention_not_team':
      return 'Só a equipe ativa desta empresa pode ser mencionada.';
    case 'mention_cannot_view':
      return 'Alguém mencionado não abre este ticket ou não vê a comunicação interna. Tire a menção ou dê acesso antes.';
    case 'mention_not_in_body':
      return 'A menção sumiu do texto: escreva @ e escolha a pessoa de novo.';
    case 'mention_requires_internal':
      return 'Menção só na comunicação interna.';
    default:
      return null;
  }
}

/**
 * O autocompletar do @ para um textarea controlado. Devolve o que o editor
 * liga: `onKeyDown` (antes do dele), `onCaret` (no change e no clique), o
 * painel e as menções a enviar.
 */
export function useMentionAutocomplete(input: {
  ticketNumber: number;
  enabled: boolean;
  value: string;
  setValue: (next: string) => void;
  textareaRef: React.RefObject<HTMLTextAreaElement | null>;
}) {
  const { ticketNumber, enabled, value, setValue, textareaRef } = input;
  const [caret, setCaret] = useState(0);
  const [picked, setPicked] = useState<PickedMention[]>([]);
  const [highlight, setHighlight] = useState(0);
  const [dismissedAt, setDismissedAt] = useState<number | null>(null);

  const active = enabled ? activeMentionQuery(value, caret) : null;
  const open = active !== null && dismissedAt !== active.start;
  const listQ = useQuery({ ...mentionableQuery(ticketNumber, active?.query.trim() ?? ''), enabled: open });
  const items = useMemo(() => (open ? (listQ.data?.items ?? []) : []), [open, listQ.data]);

  useEffect(() => setHighlight(0), [active?.query]);
  // texto esvaziado (enviado): as escolhidas saem junto
  useEffect(() => {
    if (value === '') setPicked([]);
  }, [value]);

  const choose = (index: number) => {
    const item = items[index];
    if (!item || item.blocked !== null || !active) return;
    const insert = `@${item.name} `;
    const next = value.slice(0, active.start) + insert + value.slice(caret);
    setValue(next);
    setPicked((prev) => (prev.some((p) => p.id === item.id) ? prev : [...prev, { id: item.id, name: item.name }]));
    const pos = active.start + insert.length;
    setCaret(pos);
    requestAnimationFrame(() => {
      const el = textareaRef.current;
      if (!el) return;
      el.focus();
      el.setSelectionRange(pos, pos);
    });
  };

  /** true = a tecla foi do autocompletar (o editor não a trata) */
  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>): boolean => {
    if (!open || items.length === 0) return false;
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => (h + (e.key === 'ArrowDown' ? 1 : items.length - 1)) % items.length);
      return true;
    }
    if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      choose(highlight);
      return true;
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      setDismissedAt(active!.start);
      return true;
    }
    return false;
  };

  const onCaret = (el: HTMLTextAreaElement) => setCaret(el.selectionStart ?? el.value.length);

  const current = mentionsInText(value, picked);
  const panel = open ? (
    <div
      role="listbox"
      aria-label="Mencionar alguém da equipe"
      data-tour="mencao-autocompletar"
      className="mt-1 max-h-60 overflow-y-auto rounded-md border bg-popover py-1 text-popover-foreground shadow-md"
    >
      {listQ.isPending ? (
        <p className="px-3 py-2 text-xs text-muted-foreground">Buscando na equipe…</p>
      ) : listQ.isError ? (
        <p className="px-3 py-2 text-xs text-destructive">Não foi possível buscar a equipe.</p>
      ) : items.length === 0 ? (
        <p className="px-3 py-2 text-xs text-muted-foreground">Ninguém da equipe com esse nome.</p>
      ) : (
        items.map((item, index) => (
          <button
            key={item.id}
            type="button"
            role="option"
            aria-selected={index === highlight}
            aria-disabled={item.blocked !== null}
            disabled={item.blocked !== null}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => choose(index)}
            className={cn(
              'flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm',
              index === highlight && item.blocked === null && 'bg-accent',
              item.blocked !== null ? 'cursor-not-allowed text-muted-foreground' : 'hover:bg-accent'
            )}
          >
            <AtSign className="size-3.5 shrink-0 text-primary" aria-hidden />
            <span className="truncate">{item.name}</span>
            {item.blocked ? (
              <span className="ml-auto shrink-0 text-[11px] text-amber-700">{MENTION_BLOCK_LABELS[item.blocked]}</span>
            ) : (
              <span className="ml-auto truncate text-[11px] text-muted-foreground">{item.email}</span>
            )}
          </button>
        ))
      )}
    </div>
  ) : null;

  const summary =
    current.length > 0 ? (
      <p className="flex flex-wrap items-center gap-1 text-[12px] text-muted-foreground">
        Vai avisar:
        {picked
          .filter((p) => current.includes(p.id))
          .map((p) => (
            <MentionChip key={p.id} name={p.name} />
          ))}
      </p>
    ) : null;

  return { onKeyDown, onCaret, panel, summary, mentions: current };
}

export function MentionChip({ name }: { name: string }) {
  return (
    <span className="inline-flex items-center rounded-[4px] bg-primary/10 px-1 py-px text-[0.95em] font-medium text-primary">
      @{name}
    </span>
  );
}

/**
 * O texto do comentário com os `@Nome` de quem foi mencionado em chip. A menção
 * cujo nome não está mais no texto (a pessoa foi renomeada) aparece no fim.
 */
export function MentionText({
  body,
  mentions,
  render = (text) => text,
}: {
  body: string;
  mentions: ReadonlyArray<{ userId: number; name: string }> | undefined;
  /** como o pedaço de texto puro é desenhado (ex.: com links) */
  render?: (text: string) => ReactNode;
}) {
  const list = mentions ?? [];
  if (list.length === 0) return <>{render(body)}</>;
  // os nomes mais longos primeiro: "@Ana Souza" antes de "@Ana"
  const names = [...new Set(list.map((m) => m.name))].sort((a, b) => b.length - a.length);
  const escaped = names.map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const re = new RegExp(`@(${escaped.join('|')})`, 'g');
  const parts: ReactNode[] = [];
  const found = new Set<string>();
  let last = 0;
  for (const match of body.matchAll(re)) {
    const at = match.index ?? 0;
    if (at > last) parts.push(<span key={`t${last}`}>{render(body.slice(last, at))}</span>);
    parts.push(<MentionChip key={`m${at}`} name={match[1]!} />);
    found.add(match[1]!);
    last = at + match[0].length;
  }
  if (last < body.length) parts.push(<span key={`t${last}`}>{render(body.slice(last))}</span>);
  const orphans = list.filter((m) => !found.has(m.name));
  return (
    <>
      {parts}
      {orphans.length > 0 ? (
        <span className="mt-1 flex flex-wrap items-center gap-1 text-[12px] text-muted-foreground">
          Mencionou:
          {orphans.map((m) => (
            <MentionChip key={m.userId} name={m.name} />
          ))}
        </span>
      ) : null}
    </>
  );
}

/** o textarea do editor, com o autocompletar ligado só na comunicação interna */
export function useMentionTextarea(ticketNumber: number, enabled: boolean, value: string, setValue: (v: string) => void) {
  const ref = useRef<HTMLTextAreaElement | null>(null);
  const auto = useMentionAutocomplete({ ticketNumber, enabled, value, setValue, textareaRef: ref });
  return { ref, ...auto };
}
